// Section « Conteneurs » de la fiche appareil — Docker et Podman.
//
// Les conteneurs sont regroupés par projet Compose parce que c'est ainsi qu'ils
// existent en pratique : on diagnostique une pile applicative, pas un conteneur
// isolé. Ceux qui n'appartiennent à aucun projet sont rassemblés à la fin.

import { Fragment, useState } from 'react';
import { Boxes, AlertTriangle, RotateCw } from 'lucide-react';

const tableCls = 'w-full border-collapse text-xs';
const thCls = 'px-3 py-2 text-left text-muted-2 font-medium text-[11px] uppercase border-b border-rule';
const tdCls = 'px-3 py-2 text-fg-2 border-b border-sidebar';

// « — » est réservé à l'absence de mesure ; un compteur réellement à zéro
// s'affiche « 0 o ». Docker rapporte 0B/0B pour un conteneur qui n'a pas encore
// émis de trafic, et confondre les deux ferait croire à une collecte en défaut.
const fmtBytes = (b) => {
  if (b == null) return '—';
  const units = ['o', 'Ko', 'Mo', 'Go', 'To'];
  let v = b;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
};

const fmtMB = (mb) => (mb >= 1024 ? (mb / 1024).toFixed(1) + ' Go' : Math.round(mb) + ' Mo');

// État du conteneur. Seuls « running » et « paused » sont des situations normales ;
// « exited » peut être voulu (tâche ponctuelle) ou non, d'où l'ambre plutôt que
// le rouge, réservé aux états qui signalent une panne.
const STATE_CLS = {
  running: 'bg-ok/15 text-emerald-300',
  paused: 'bg-blue-500/15 text-blue-400',
  exited: 'bg-warn/15 text-amber-300',
  created: 'bg-slate-500/15 text-muted',
  restarting: 'bg-crit/20 text-red-300',
  dead: 'bg-crit/20 text-red-300',
};

const HEALTH_CLS = {
  healthy: 'bg-ok/15 text-emerald-300',
  unhealthy: 'bg-crit/20 text-red-300',
  starting: 'bg-blue-500/15 text-blue-400',
};

export function ContainersPanel({ inv, sectionCls, sTitleCls }) {
  const containers = inv?.containers || [];
  const engines = inv?.containerEngines || '';
  const [showStopped, setShowStopped] = useState(false);

  // Aucun moteur installé : la section entière disparaît plutôt que d'afficher un
  // zéro qui ferait croire à une absence de conteneurs sur une machine qui n'en
  // héberge simplement pas.
  if (!engines && containers.length === 0) return null;

  const running = containers.filter((c) => c.state === 'running');
  const problems = containers.filter((c) => c.health === 'unhealthy' || c.state === 'restarting');
  const visible = showStopped ? containers : containers.filter((c) => c.state === 'running');
  const hiddenCount = containers.length - running.length;

  // Regroupement par projet Compose, ordre alphabétique, « hors projet » en dernier.
  const groups = new Map();
  for (const c of visible) {
    const key = c.composeProject || '';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(c);
  }
  const orderedGroups = [...groups.entries()].sort(([a], [b]) => {
    if (a === '') return 1;
    if (b === '') return -1;
    return a.localeCompare(b);
  });

  return (
    <div className={sectionCls}>
      <div className={`${sTitleCls} flex items-center flex-wrap gap-2`}>
        <span className="inline-flex items-center gap-2">
          <Boxes size={16} strokeWidth={1.75} /> Conteneurs
          {engines && (
            <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-rule text-muted">
              {engines}
            </span>
          )}
        </span>
        <span className="ml-auto text-xs font-bold font-mono text-fg-2">
          {running.length} / {containers.length} actifs
        </span>
      </div>

      {problems.length > 0 && (
        <div className="mb-3 bg-crit/10 border border-crit/30 rounded-lg px-3.5 py-2.5 flex items-start gap-2">
          <AlertTriangle size={15} strokeWidth={2} className="text-red-300 shrink-0 mt-0.5" />
          <div className="text-[12px]">
            <div className="font-semibold text-red-300">
              {problems.length} conteneur{problems.length > 1 ? 's' : ''} en anomalie
            </div>
            <div className="text-fg-2">
              {problems.map((c) => c.name).join(', ')}
            </div>
          </div>
        </div>
      )}

      {containers.length === 0 ? (
        <div className="text-muted-2 text-xs">Aucun conteneur sur cet hôte.</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className={tableCls}>
              <thead>
                <tr>
                  {['Conteneur', 'Image', 'État', 'CPU', 'Mémoire', 'Réseau ↓/↑', 'Ports'].map((c) => (
                    <th key={c} className={thCls}>
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orderedGroups.map(([project, items]) => (
                  <Fragment key={project || '__none__'}>
                    {orderedGroups.length > 1 && (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-3 pt-3 pb-1 text-[10.5px] uppercase tracking-wide text-muted-2 font-bold"
                        >
                          {project || 'Hors projet Compose'}
                        </td>
                      </tr>
                    )}
                    {items.map((c) => (
                      <tr key={c.engine + c.id}>
                        <td className={`${tdCls} font-semibold`}>
                          <div className="font-mono">{c.composeService || c.name}</div>
                          <div className="text-[10px] text-muted-2 font-mono">
                            {c.id}
                            {c.composeService ? ` · ${c.name}` : ''}
                          </div>
                        </td>
                        <td className={`${tdCls} text-muted max-w-[200px] truncate`} title={c.image}>
                          {c.image}
                        </td>
                        <td className={tdCls}>
                          <div className="flex flex-col gap-1 items-start">
                            <span
                              className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                                STATE_CLS[c.state] || 'bg-slate-500/15 text-muted'
                              }`}
                              title={c.status}
                            >
                              {c.state}
                            </span>
                            {/* « none » = aucune sonde définie : ce n'est pas un
                                défaut, donc rien à afficher. */}
                            {c.health && c.health !== 'none' && (
                              <span
                                className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                                  HEALTH_CLS[c.health] || 'bg-slate-500/15 text-muted'
                                }`}
                              >
                                {c.health}
                              </span>
                            )}
                            {c.restartCount > 0 && (
                              <span
                                className="px-2 py-0.5 rounded text-[10px] font-semibold bg-warn/15 text-amber-300 inline-flex items-center gap-1"
                                title="Redémarrages cumulés depuis la création du conteneur"
                              >
                                <RotateCw size={10} strokeWidth={2.5} /> {c.restartCount}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className={`${tdCls} font-mono text-[11px]`}>
                          {c.state === 'running' ? c.cpuPercent.toFixed(2) + '%' : '—'}
                        </td>
                        <td className={`${tdCls} font-mono text-[11px]`}>
                          {c.state === 'running' && c.memoryLimitMB > 0 ? (
                            <>
                              {fmtMB(c.memoryUsedMB)}
                              <span className="text-muted-2"> / {fmtMB(c.memoryLimitMB)}</span>
                              <div className="text-[10px] text-muted-2">{c.memoryPercent.toFixed(1)}%</div>
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className={`${tdCls} font-mono text-[11px] whitespace-nowrap`}>
                          {c.state === 'running'
                            ? `${fmtBytes(c.networkRxBytes)} / ${fmtBytes(c.networkTxBytes)}`
                            : '—'}
                        </td>
                        <td className={`${tdCls} text-[11px] font-mono max-w-[170px] truncate`} title={c.ports}>
                          {c.ports || '—'}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {hiddenCount > 0 && (
            <button
              onClick={() => setShowStopped((s) => !s)}
              className="mt-3 bg-transparent border border-rule-3 rounded-lg px-3 py-1.5 text-muted text-[11px] cursor-pointer hover:bg-rule"
            >
              {showStopped
                ? 'Masquer les conteneurs arrêtés'
                : `Afficher les ${hiddenCount} conteneur${hiddenCount > 1 ? 's' : ''} arrêté${hiddenCount > 1 ? 's' : ''}`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
